import React from 'react';
import { Badge, ReadOnlyChip, RobbieMark } from '../ui';
import { CriteriaChip } from '../../utils/criteriaChips';
import { useRowOverflow } from '../../hooks/useRowOverflow';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * "Sourcing with these in mind": the buyer's step-2 criteria, shown back to
 * them where the research happens, so nobody wonders whether they were heard.
 *
 * Neutral chips are what the buyer chose; Robbie-tone chips are Robbie's
 * reading of their free-text notes, each quoting its source on hover. Until
 * those exist (or if they never will) the notes themselves sit among the
 * neutral chips, one line long. Chips
 * only — read-only, with one way out: "Ask Robbie to change".
 *
 * `full` wraps every chip (the Smart BOM sheet); `compact` keeps to one line
 * — at most the first four chips, fewer if the line is narrower — and folds
 * the rest into "+N" (the checkpoint and the reveal card). On a phone compact
 * reads as full: "+N" hides its chips behind a hover, and a touch screen has
 * none.
 *
 * Renders nothing when there is nothing to show. Presentational — see
 * `RfqCriteriaBrief` for the wired version.
 */
export type CriteriaBriefVariant = 'full' | 'compact';
/** card: its own panel · strip: a full-bleed footer band · none: sits on a Robbie surface already. */
export type CriteriaBriefSurface = 'card' | 'strip' | 'none';

export interface CriteriaBriefProps {
  /** The buyer's own criteria: the structured ones, plus their notes while Robbie has no pointers. */
  chips: CriteriaChip[];
  /** Robbie's pointers from the notes. */
  noteChips: CriteriaChip[];
  variant?: CriteriaBriefVariant;
  surface?: CriteriaBriefSurface;
  /**
   * Show the "Sourcing with these in mind" text beside the mark (default).
   * Off only where the copy right above already names the criteria, so a
   * narrow dialog keeps its line for chips; the region keeps the name.
   */
  labelled?: boolean;
  /** Omitted where the buyer cannot talk to Robbie — no link that does nothing. */
  onAskRobbie?: () => void;
  className?: string;
}

const COMPACT_LIMIT = 4;

const SURFACES: Record<CriteriaBriefSurface, string> = {
  card: 'rounded-card border border-robbie-border-soft bg-ai-panel px-s4 py-s3',
  strip: 'border-t border-robbie-border-soft bg-ai-panel px-s6 py-s3',
  none: '',
};

type Entry = { chip: CriteriaChip; robbie: boolean };

const ChipFor: React.FC<Entry> = ({ chip, robbie }) =>
  robbie ? (
    <Badge tone="robbie" chip title={chip.title}>
      {chip.value}
    </Badge>
  ) : (
    <ReadOnlyChip label={chip.label} title={chip.title} truncate={chip.truncate}>
      {chip.value}
    </ReadOnlyChip>
  );

const foldedTitle = (folded: Entry[]) =>
  folded.map(({ chip }) => (chip.label ? `${chip.label} ${chip.value}` : chip.value)).join(' · ');

export const CriteriaBrief: React.FC<CriteriaBriefProps> = ({
  chips,
  noteChips,
  variant = 'full',
  surface = 'card',
  labelled = true,
  onAskRobbie,
  className = '',
}) => {
  const entries: Entry[] = [
    ...chips.map((chip) => ({ chip, robbie: false })),
    ...noteChips.map((chip) => ({ chip, robbie: true })),
  ];
  const isMobile = useIsMobile();
  const compact = variant === 'compact' && !isMobile;
  const rowEntries = compact ? entries.slice(0, COMPACT_LIMIT) : entries;
  const { rowRef, visibleCount } = useRowOverflow(rowEntries.length);
  if (entries.length === 0) return null;

  const folded = compact ? entries.slice(Math.min(visibleCount, rowEntries.length)) : [];

  return (
    <section
      aria-label="Sourcing with these in mind"
      className={`flex items-center gap-s2 ${compact ? '' : 'flex-wrap'} ${SURFACES[surface]} ${className}`}
    >
      <span className="mr-s1 inline-flex shrink-0 items-center gap-s2 text-meta font-semibold text-robbie-ink">
        <RobbieMark size={compact ? 'xxs' : 'xs'} />
        {labelled && 'Sourcing with these in mind'}
      </span>
      <div
        ref={rowRef}
        className={compact
          ? 'flex h-7 min-w-0 flex-1 flex-wrap gap-s2 overflow-hidden'
          : 'contents'}
      >
        {rowEntries.map((entry) => (
          <ChipFor key={entry.chip.key} {...entry} />
        ))}
      </div>
      {folded.length > 0 && (
        <ReadOnlyChip title={foldedTitle(folded)} className="shrink-0">
          +{folded.length}
        </ReadOnlyChip>
      )}
      {onAskRobbie && (
        <button
          type="button"
          onClick={onAskRobbie}
          className={`shrink-0 whitespace-nowrap rounded-control text-meta font-medium text-action hover:underline
            focus:outline-none focus-visible:ring-2 focus-visible:ring-action ${compact ? 'ml-s1' : 'ml-auto'}`}
        >
          Ask Robbie to change
        </button>
      )}
    </section>
  );
};

export default CriteriaBrief;
